/**
 * Utilitário para extrair e formatar mensagens de erro da API
 * Garante que o usuário veja mensagens claras em português
 */

interface ApiErrorResponse {
  statusCode?: number;
  error?: string;
  messages?: Record<string, string[]>;
  message?: string | string[];
}

type ErrorPayload = ApiErrorResponse & { data?: ApiErrorResponse };

/**
 * Extrai mensagens de erro da resposta da API e formata em português
 * @param error - O erro capturado da API
 * @param fallbackMessage - Mensagem padrão caso não consiga extrair o erro
 * @returns String formatada com a(s) mensagem(ns) de erro
 */
export const extractApiErrorMessages = (
  error: unknown,
  fallbackMessage = 'Ocorreu um erro inesperado. Tente novamente.'
): string => {
  try {
    // A camada HTTP pode lançar o body da resposta diretamente ou um AxiosError.
    // Ambos os formatos precisam preservar a mensagem da seguradora.
    const requestError = error as {
      response?: { data?: ErrorPayload };
      data?: ErrorPayload;
    };
    const data: ErrorPayload | undefined =
      requestError?.response?.data || requestError?.data || (error as ErrorPayload);

    // Se tiver messages (formato de validação do NestJS)
    if (data?.messages && typeof data.messages === 'object') {
      const allMessages: string[] = [];

      for (const [field, messages] of Object.entries(data.messages)) {
        if (Array.isArray(messages)) {
          allMessages.push(...messages);
        }
      }

      if (allMessages.length > 0) {
        return allMessages.join('\n');
      }
    }

    // Se tiver message (pode ser string ou array)
    if (data?.message) {
      if (Array.isArray(data.message)) {
        return data.message.join('\n');
      }
      return data.message;
    }

    // Se tiver error
    if (data?.error) {
      return translateCommonErrors(data.error);
    }

    if (data?.data) {
      return extractApiErrorMessages(data.data, fallbackMessage);
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return fallbackMessage;
  } catch {
    return fallbackMessage;
  }
};

/**
 * Traduz erros comuns de HTTP para português
 */
const translateCommonErrors = (error: string): string => {
  const translations: Record<string, string> = {
    'Bad Request': 'Requisição inválida. Verifique os dados informados.',
    'Unauthorized': 'Não autorizado. Faça login novamente.',
    'Forbidden': 'Você não tem permissão para realizar esta ação.',
    'Not Found': 'Recurso não encontrado.',
    'Internal Server Error': 'Erro interno do servidor. Tente novamente mais tarde.',
    'Service Unavailable': 'Serviço temporariamente indisponível. Tente novamente.',
  };

  return translations[error] || error;
};

/**
 * Exibe notificação de erro formatada
 * Usar em conjunto com dangerNotification
 */
export const getFormattedErrorMessage = (
  error: unknown,
  context?: string
): { title: string; message: string } => {
  const errorMessage = extractApiErrorMessages(error);

  return {
    title: context ? `Erro: ${context}` : 'Erro',
    message: errorMessage
  };
};
